import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { Link } from 'react-router-dom';
import { Play, Lock, Crown, Clock, Eye } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ShareButtons } from '../components/ShareButtons';
import { LikeButton, CommentSection } from '../components/SocialFeatures';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default function VideosPage() {
  const { isAuthenticated } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    fetchData();
  }, [isAuthenticated]);

  const fetchData = async () => {
    try {
      const [videosRes] = await Promise.all([
        axios.get(`${API_URL}/videos`)
      ]);
      setVideos(videosRes.data);
      
      if (isAuthenticated) {
        const subRes = await axios.get(`${API_URL}/subscription`);
        setSubscription(subRes.data);
      }
    } catch (error) {
      logger.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const isPremium = subscription?.tier === 'premium';

  const getEmbedUrl = (url, type) => {
    if (type === 'embed') {
      // Convert YouTube/Vimeo URLs to embed format
      if (url.includes('youtube.com/watch')) {
        const videoId = url.split('v=')[1]?.split('&')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1]?.split('?')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      if (url.includes('vimeo.com/')) {
        const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
        return `https://player.vimeo.com/video/${videoId}`;
      }
    }
    return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="main-content pt-24" data-testid="videos-page">
      <div className="container py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center">
              <Play className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Videos</h1>
              <p className="text-gray-400">Music videos & exclusive content</p>
            </div>
          </div>
          
          {!isPremium && (
            <Link to="/subscription" className="btn-primary flex items-center gap-2" data-testid="upgrade-premium">
              <Crown className="w-4 h-4" /> Upgrade to Premium
            </Link>
          )}
        </div>

        {videos.length === 0 ? (
          <div className="empty-state glass-card py-16">
            <Play className="empty-state-icon w-16 h-16" />
            <h3 className="text-xl font-semibold mb-2">No videos yet</h3>
            <p className="text-gray-400">Check back soon for new video content!</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Video List */}
            <div className="lg:col-span-1 space-y-4">
              {videos.map(video => (
                <div
                  key={video.id}
                  onClick={() => !video.locked && setSelectedVideo(video)}
                  className={`glass-card p-4 cursor-pointer transition-all ${
                    selectedVideo?.id === video.id ? 'border-purple-500 neon-glow' : ''
                  } ${video.locked ? 'opacity-60' : 'hover:border-white/20'}`}
                  data-testid={`video-item-${video.id}`}
                >
                  <div className="relative">
                    <img 
                      src={video.thumbnail_url} 
                      alt={video.title}
                      className="w-full aspect-video rounded-lg object-cover mb-3"
                    />
                    {video.locked && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-lg">
                        <div className="text-center">
                          <Lock className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                          <span className="text-sm text-yellow-400">Premium Only</span>
                        </div>
                      </div>
                    )}
                    {video.is_exclusive && !video.locked && (
                      <div className="absolute top-2 right-2 px-2 py-1 bg-yellow-500/80 rounded text-xs font-medium flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Exclusive
                      </div>
                    )}
                    {video.price > 0 && (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-purple-500/80 rounded text-xs font-medium">
                        ${video.price.toFixed(2)}
                      </div>
                    )}
                  </div>
                  <h3 className="font-semibold truncate">{video.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-400 mt-1">
                    {video.duration > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatDuration(video.duration)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" /> {video.views || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Video Player */}
            <div className="lg:col-span-2">
              {selectedVideo ? (
                <div className="glass-card p-6 sticky top-24">
                  <div className="aspect-video rounded-lg overflow-hidden bg-black mb-4">
                    {selectedVideo.video_type === 'embed' ? (
                      <iframe
                        src={getEmbedUrl(selectedVideo.video_url, selectedVideo.video_type)}
                        title={selectedVideo.title}
                        className="w-full h-full"
                        allowFullScreen
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      />
                    ) : (
                      <video
                        src={selectedVideo.video_url}
                        controls
                        className="w-full h-full"
                        poster={selectedVideo.thumbnail_url}
                      />
                    )}
                  </div>
                  
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold">{selectedVideo.title}</h2>
                      <p className="text-gray-400 mt-1">{selectedVideo.artist}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <LikeButton contentType="video" contentId={selectedVideo.id} />
                      <ShareButtons title={selectedVideo.title} type="video" />
                    </div>
                  </div>
                  
                  <p className="text-gray-300">{selectedVideo.description}</p>
                  
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" /> {selectedVideo.views || 0} views
                    </span>
                    {selectedVideo.is_exclusive && (
                      <span className="flex items-center gap-1 text-yellow-400">
                        <Crown className="w-4 h-4" /> Premium Content
                      </span>
                    )}
                  </div>

                  {/* Comments */}
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <CommentSection contentType="video" contentId={selectedVideo.id} />
                  </div>
                </div>
              ) : (
                <div className="glass-card p-12 text-center sticky top-24">
                  <Play className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">Select a video to watch</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
